import { PureComponent } from "react";

import { MarkupColumnTypes } from "@btnet/services-btnet/handlers/models/markupColumnTypes";
import { cn } from "@btnet/utilities-core";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { recalculateAllFields } from "utilities/lineItem/markupUtility";

import { BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { Currency } from "commonComponents/financial/Currency/Currency";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import { LineItemCurrencyFooter } from "commonComponents/utilities/LineItemContainer/cellImplementations/ReadOnlyCurrencyCell";
import {
    type IPreviewLineItemProps,
    LineItemPreview,
} from "commonComponents/utilities/LineItemContainer/LineItemPreview";
import { GridLineItemColumnType } from "commonComponents/utilities/LineItemContainer/types/LineItem.enums";
import {
    LineItemColumns,
    LineItemConstants,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import { type IMarkupLineItem } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

export interface IOwnerPriceCell extends IMarkupLineItem, IPreviewLineItemProps {
    /**
     * @default true
     */
    isBold?: boolean;
}

export class OwnerPriceCell extends PureComponent<IGridCellRenderProps<IOwnerPriceCell>> {
    static contextType = BuilderInfoContext;
    declare context: React.ContextType<typeof BuilderInfoContext>;

    private handleChange = (fieldName: string, value: any) => {
        const { cellValue, field, onChange } = this.props;

        const calculatedFields = recalculateAllFields({ ...cellValue!, ownerPrice: value }, true);
        onChange(field.path, { ...cellValue!, ...calculatedFields });
    };

    render() {
        const { cellValue, field, isInEdit, onBlur, canEdit, autoFocus, onFocus } = this.props;

        if (!cellValue) {
            return null;
        }

        const ownerPriceFieldName = `${field.path}.ownerPrice`;
        const readOnly = !isInEdit || !canEdit;
        const isBold = cellValue.isBold ?? true;

        const component = (
            <LineItemPreview
                fieldName="ownerPrice"
                addPreviewListener={cellValue.addPreviewListener}
                render={(preview) => (
                    <Currency<undefined>
                        key={preview?.ownerPrice ?? "ownerPrice"}
                        id={ownerPriceFieldName}
                        min={LineItemConstants.Currency.MIN}
                        max={LineItemConstants.Currency.MAX}
                        data-testid={ownerPriceFieldName}
                        value={preview?.ownerPrice ?? cellValue.ownerPrice}
                        readOnly={readOnly}
                        disabled={
                            (preview?.markupType ?? cellValue.markupType) !==
                            MarkupColumnTypes.OwnerPrice
                        }
                        onChange={this.handleChange}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        decimalScale={2}
                        autoFocus={autoFocus}
                        className={preview ? "LineItemPreview" : undefined}
                    />
                )}
            />
        );

        if (isInEdit) {
            return (
                <BTFormItemAutomatic
                    id={ownerPriceFieldName}
                    className={cn(
                        "LineItemCell",
                        {
                            RightAlignFormLabel: !canEdit,
                        },
                        { ellipsis: cellValue?.ellipsis },
                    )}
                >
                    {component}
                </BTFormItemAutomatic>
            );
        }

        return (
            <div
                className={cn(
                    "LineItemCell",
                    {
                        lineItemEditableCell:
                            canEdit && cellValue.markupType === MarkupColumnTypes.OwnerPrice,
                        "text-bold": !isInEdit && isBold,
                    },
                    { ellipsis: cellValue?.ellipsis },
                )}
                tabIndex={canEdit ? 0 : undefined}
                onFocus={onFocus}
            >
                {component}
            </div>
        );
    }
}

export const ownerPriceCellDefaultConfig = () => ({
    id: LineItemColumns.OwnerPrice,
    name: "Client Price",
    jsonKey: "ownerPrice",
    "data-testid": "ownerPrice",
    width: 160,
    minWidth: 48,
    ellipsis: true,
    align: "right",
    isSortable: true,
    columnType: GridLineItemColumnType.ownerPrice,
    isFrozen: false,
    isHidden: false,
    Cell: OwnerPriceCell,
    FooterCell: LineItemCurrencyFooter,
});
